import { cx } from '@/utils/strings'
import { ReactNode } from 'react'
import backgroundStyles from '@/constants/background-styles'

export interface GradientBackgroundWrapperProps {
  readonly id?: string
  readonly children: ReactNode
  readonly backgroundStyle?: string
  readonly unconstrained?: boolean
  readonly className?: string
  readonly innerClassName?: string
  readonly containerClassName?: string
}

export default function GradientBackgroundWrapper({
  id,
  children,
  backgroundStyle,
  unconstrained = false,
  className = '',
  innerClassName = '',
  containerClassName = '',
}: GradientBackgroundWrapperProps) {
  // Determine if we should apply gradient border effect

  const acceptedBgs =
    backgroundStyle === backgroundStyles.gradientWithCurves1 ||
    backgroundStyle === backgroundStyles.gradientWithCurves2 ||
    backgroundStyle === backgroundStyles.gradientWithCurves3 ||
    backgroundStyle === backgroundStyles.gradient1 ||
    backgroundStyle === backgroundStyles.gradient2 ||
    backgroundStyle === backgroundStyles.midnightGradient

  const shouldApplyGradientBorder =
    (!unconstrained && backgroundStyle === backgroundStyles.gradientWithCurves1) ||
    (!unconstrained && backgroundStyle === backgroundStyles.gradientWithCurves2) ||
    (!unconstrained && backgroundStyle === backgroundStyles.gradientWithCurves3) ||
    (!unconstrained && backgroundStyle === backgroundStyles.gradient1) ||
    (!unconstrained && backgroundStyle === backgroundStyles.gradient2)

  // Check if outer div needs any classes
  const hasOuterDivClasses = shouldApplyGradientBorder || className.trim() !== ''

  // Check if inner div needs any classes
  const hasInnerDivClasses = acceptedBgs || innerClassName.trim() !== ''

  // If no wrapper divs are needed, just render children in section
  if (!hasOuterDivClasses && !hasInnerDivClasses) {
    return (
      <section id={id} className={cx(!unconstrained && backgroundStyle && 'max-w-8xl mx-auto', containerClassName)}>
        {children}
      </section>
    )
  }

  // If only inner div is needed
  if (!hasOuterDivClasses && hasInnerDivClasses) {
    return (
      <section id={id} className={cx(!unconstrained && backgroundStyle && 'max-w-8xl mx-auto', containerClassName)}>
        <div
          className={cx(
            backgroundStyle && 'relative overflow-hidden',
            backgroundStyle?.includes('Gradient With Curves') &&
              'rounded-lg relative z-3 before:content-[""] before:absolute before:bg-cover before:bg-center before:bg-no-repeat before:opacity-100 before:-z-1 before:top-0 before:left-0 before:right-0 before:bottom-0',
            backgroundStyle === backgroundStyles.gradientWithCurves1 && 'gradient-curve-1',
            backgroundStyle === backgroundStyles.gradientWithCurves2 && 'gradient-curve-2',
            backgroundStyle === backgroundStyles.gradientWithCurves3 && 'gradient-curve-3',
            (backgroundStyle === backgroundStyles.gradient1 || backgroundStyle === backgroundStyles.gradient2) &&
              'relative z-3',
            backgroundStyle === backgroundStyles.gradient1 && 'gradient-bg-1',
            backgroundStyle === backgroundStyles.gradient2 && 'gradient-bg-2',
            backgroundStyle === backgroundStyles.midnightGradient && 'bg-midnight-gradient',
            !unconstrained && 'rounded-lg',
            innerClassName
          )}
        >
          {children}
        </div>
      </section>
    )
  }

  // Render both divs when needed
  return (
    <section id={id} className={cx(!unconstrained && backgroundStyle && 'max-w-8xl mx-auto', containerClassName)}>
      <div
        className={cx(
          !unconstrained && 'rounded-lg after:rounded-lg',
          shouldApplyGradientBorder &&
            'mx-5 lg:mx-9 bw-gradient-border-effect-1 relative p-[1px] after:z-1 after:content:[""] after:absolute after:top-[1px] after:left-[1px] after:right-[1px] after:bottom-[1px] after:bg-white dark:after:bg-secondary-900',
          className
        )}
      >
        {hasInnerDivClasses ? (
          <div
            className={cx(
              'overflow-hidden relative z-3',
              backgroundStyle?.includes('Gradient With Curves') &&
                'rounded-lg relative z-3 before:content-[""] before:absolute before:bg-cover before:bg-center before:bg-no-repeat before:opacity-100 before:-z-1 before:top-0 before:left-0 before:right-0 before:bottom-0',
              backgroundStyle === backgroundStyles.gradientWithCurves1 && 'gradient-curve-1',
              backgroundStyle === backgroundStyles.gradientWithCurves2 && 'gradient-curve-2',
              backgroundStyle === backgroundStyles.gradientWithCurves3 && 'gradient-curve-3',
              backgroundStyle === backgroundStyles.gradient1 && 'gradient-bg-1',
              backgroundStyle === backgroundStyles.gradient2 && 'gradient-bg-2',
              backgroundStyle === backgroundStyles.midnightGradient && 'bg-midnight-gradient',
              !unconstrained && 'rounded-lg',
              innerClassName
            )}
          >
            {children}
          </div>
        ) : (
          children
        )}
      </div>
    </section>
  )
}
